import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ContentChild,
    ElementRef,
    HostBinding,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    SimpleChanges,
    TemplateRef,
    ViewEncapsulation,
} from '@angular/core';

/**
 * MvsReviewWorkbenchComponent — the shared "Review Workbench" layout (skill: fe_core_review-workbench).
 *
 * The recurring Alpha pattern for a runtime review/processing UI: a persistent left worklist/table
 * (created ONCE, reloaded in place) and a right detail/tooling "Workbench" whose CONTENT is conditional
 * on selection, separated by a movable PrimeNG splitter. Optionally a third (center) pane.
 *
 * This component owns the fragile LAYOUT FRAME only — the viewport-height host binding, the splitter,
 * the per-pane full-height flex column and the `::ng-deep .p-splitter-panel` scroll chain — so consumers
 * stop copy-pasting it (and getting the `min-height:0` / viewport-height parts wrong). Consumers project
 * each pane's CONTENT via the `#workbenchList` / `#workbenchDetail` (+ optional `#workbenchCenter`)
 * named templates; each projected root is the flex child of the pane and owns its own scroll (typically a
 * `surface-card` panel with `overflow-y:auto`).
 *
 * ── HEIGHT CONTRACT (the SINGLE canonical description; the template and the stylesheet only point here)
 *
 * There is exactly ONE place that decides the host height — the `hostHeight` getter below — and it
 * resolves in this precedence order:
 *
 *   1. `autoFitViewport === true` AND a measurement exists
 *      → `{measured}px`, where
 *        `measured = max(0, floor(document.documentElement.clientHeight - hostRect.top - gapPx))`
 *        and `gapPx = viewportBottomGapRem * rootFontSizePx` (both resolved AT MEASURE TIME).
 *        `viewportOffsetPx` is IGNORED in this mode.
 *   2. `viewportOffsetPx != null` → `calc(100vh - {viewportOffsetPx}px)` (the original static mode).
 *   3. otherwise → `100%` (fill the parent, which must then be height-bound).
 *
 * `clientHeight` (not `window.innerHeight`) is deliberate: `innerHeight` includes the horizontal
 * scrollbar gutter, so a document-level horizontal scrollbar would otherwise reintroduce the vertical
 * overflow this mode exists to remove.
 *
 * Auto-fit exists because the chrome ABOVE the host is not constant: the app shell renders the
 * breadcrumb as an optional sibling above `.layout-content`, so the same route measures a different
 * host top depending on whether it was entered by hard reload or by client-side navigation. Any single
 * hardcoded `viewportOffsetPx` is therefore wrong in one of the two states. Auto-fit is OPT-IN: a
 * consumer that sets neither new input keeps byte-identical behaviour — no listener, no observer, no
 * animation frame, and branches 2 and 3 render the exact same string as before.
 *
 * This is the RUNTIME counterpart to — and NOT — the deprecated config master-detail (see fe_core_page).
 */
@Component({
    selector: 'mvs-review-workbench',
    templateUrl: './mvs-review-workbench.component.html',
    styleUrls: ['./mvs-review-workbench.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
    standalone: false,
})
export class MvsReviewWorkbenchComponent implements AfterViewInit, OnChanges, OnDestroy {

    /**
     * Number of panes: 2 (default), 3 (enables the `#workbenchCenter` slot), or 1.
     *
     * `1` shows ONLY the worklist, full width, with no gutter — for hosts that have nothing to put in the
     * detail pane yet. It is designed to be TOGGLED AT RUNTIME (e.g. `[panes]="selectedId ? 2 : 1"`), and
     * that is exactly why 1 and 2 render the SAME two-panel splitter, with the detail pane and the gutter
     * collapsed by layout CSS in 1-pane mode, instead of a separate single-panel splitter.
     *
     * The reason is a hard constraint, not a shortcut. PrimeNG's Splitter builds its `panels` array ONCE,
     * in `onAfterContentInit`, and never re-reads its `pTemplate` children — so a splitter cannot gain or
     * lose a pane after creation. Rendering 1-pane from its own template branch would therefore mean
     * destroying one splitter and creating another on every toggle, which destroys the projected
     * `#workbenchList` view with it: the worklist table would be re-created and re-fetch its data on the
     * first selection, visibly flashing and losing the user's place in the queue. Keeping the structure
     * stable is what makes the worklist survive the toggle (skill R2), which is the whole point.
     *
     * 2- and 3-pane consumers are unaffected: `panes === 1` is false for them, so no collapse class is
     * applied and the rendered markup is identical to before this input accepted 1.
     */
    @Input() panes: 1 | 2 | 3 = 2;

    /**
     * OPT-IN, direction-selective collapse of ONE pane. `'list'` collapses the FIRST (worklist) pane and
     * gives its width to the rest; `'detail'` collapses the LAST (workbench) pane. `null`/unset is the
     * default and means "no pane is collapsed".
     *
     * NOTHING IS UNMOUNTED. Collapsing is pure CSS on the splitter's own panel elements (the `rw-collapse-*`
     * classes below mirror the existing `rw-single` mechanic, only direction-selective): the collapsed
     * panel is taken out of the flex distribution (`flex: 0 0 0`) and hidden (`visibility: hidden`), and its
     * adjacent gutter is `display: none`. Both projected `ng-template` slots keep their embedded views, so
     * a search term, a keyboard cursor and a half-finished inline edit all survive the toggle. That is a
     * HARD requirement, not an optimisation — toggling is a frequent user interaction, and a feature that
     * discards in-progress input on every toggle is unusable. For the same reason this must never become an
     * `*ngIf`, and content must never be moved between slots.
     *
     * SPLITTER STATE SURVIVES. PrimeNG writes the pane widths as INLINE `flex-basis` (initially and on every
     * gutter drag) and keeps its own `panelSizes` state; none of that is touched here — the collapse rules
     * only add author `!important` declarations, which outrank a non-important inline style while they
     * apply and yield the inline (possibly user-dragged) width back the moment they stop. So
     * `null → 'list' → null` restores exactly the width the user had dragged to.
     *
     * ORTHOGONAL TO `panes`, with two defined interactions:
     *   * `panes === 1` — IGNORED. 1-pane mode already collapses the detail pane via `rw-single`; honouring
     *     `collapsePane` on top of it could collapse BOTH panes and render an empty splitter.
     *   * `panes === 3` — honoured: `'list'` collapses the first pane, `'detail'` the last, and the two
     *     REMAINING panes share the freed width in proportion to their current sizes (plain flex-grow) —
     *     i.e. "full width" for the survivor is a 2-pane statement, not a 3-pane one. The center pane can
     *     not be collapsed; there is no requirement for it and no unambiguous name for it.
     *
     * `undefined` (never bound) vs `null` (bound to nothing) are both "no collapse", but they are NOT the
     * same for rendering: the collapse TRANSITION stylesheet is gated on the input having been bound at all
     * (`collapsible` below), so a consumer that does not use this feature renders byte-identically to
     * before it existed — no transition on `.p-splitterpanel`, and therefore no change to how a gutter drag
     * feels.
     */
    @Input() collapsePane?: 'list' | 'detail' | null;

    /** Initial pane sizes in %. Defaults: 2-pane `[66, 34]`, 3-pane `[22, 48, 30]`. */
    @Input() panelSizes?: number[];

    /**
     * Minimum pane sizes in %. Defaults: 2-pane `[30, 22]`, 3-pane `[15, 35, 25]`.
     *
     * INERT WHEN {@link resizable} IS FALSE. PrimeNG reads this array in exactly one place — the
     * `validateResize` it runs while a drag is in flight — so with no drag there is nothing to validate
     * and no code path that ever looks at it. It is still handed to the splitter (uniformly, like every
     * other consumer's), but a non-resizable consumer should not bind it: a bound input that cannot
     * affect anything is a claim its next reader has to disprove.
     */
    @Input() minSizes?: number[];

    /**
     * Whether the gutter between the panes can be DRAGGED. Defaults to true — the behaviour every
     * consumer has today — so the 29 existing templates are untouched: the `rw-no-resize` class below is
     * emitted only for a consumer that explicitly binds `[resizable]="false"`, and every rule of the
     * fixed-split band requires it. Named for the CAPABILITY rather than its absence so the call site
     * reads as one negation (`[resizable]="false"`) instead of two, and so it matches the word PrimeNG
     * itself uses for this (`p-dialog [resizable]`, `pResizableColumn`).
     *
     * Opt out when the split is a LAYOUT DECISION the page has already made, not a preference it is
     * offering: the cw case-create screen has a fixed 34/66 reading column and a detail column, and a
     * drag handle there invites the user to fix something that is not broken.
     *
     * <h3>The gutter SURVIVES as the gap — it is not removed</h3>
     * The first instinct is `display: none` on the gutter, the way {@link panes} `=== 1` and
     * {@link stackBelowLg} both do it. That is wrong here for two measured reasons.
     * <ul>
     *   <li>THE GUTTER IS THE ONLY SEPARATION. Its grey comes from its own background
     *       (`splitter.gutter.background`, and again from the legacy designer theme's
     *       `$splitterGutterBg`), while `.p-splitter`'s background is the SAME content white as the
     *       `surface-card` panels consumers project. Remove the gutter and two white cards meet over a
     *       white ground — no seam, just the notch where their two radii touch.</li>
     *   <li>A CONTAINER GAP WOULD SURVIVE A COLLAPSE. Replacing it with `column-gap` on the splitter
     *       looks equivalent and is not: a collapsed pane is still a flex item, so its gap stays and the
     *       surviving pane starts one gap in from the edge — the wart the stacked band accepts for
     *       `row-gap` and states in its own comment. On this axis that would inset the full-width pane
     *       permanently, in the state a collapsing consumer spends most of its life in.</li>
     * </ul>
     * So the gutter stays a flex item and becomes a transparent 1rem spacer, and three things follow for
     * free: {@link collapsePane} already removes the gutter ADJACENT to a collapsed pane, so the spacer
     * disappears exactly when its pane does; PrimeNG's `calc(N% - Mpx)` already has a term for a gutter,
     * so the sizing model is unchanged; and the stacked band's own `display: none` + `row-gap` still wins
     * below `lg`, so stacked renders exactly as it does today. 1rem is that band's `row-gap` and not a
     * new number — the same two cards, the same distance apart, whichever way they are laid out.
     *
     * <h3>Removing the AFFORDANCE is not removing the INTERACTION, and there are two of them</h3>
     * PrimeNG binds the POINTER path (`mousedown`, `touchstart`, `touchmove`, `touchend`) on the gutter
     * itself, but the KEYBOARD path on a separate child: `.p-splitter-gutter-handle` carries
     * `tabindex="0"`, `keydown`/`keyup` and `aria-valuenow`, i.e. the gutter is arrow-key resizable and
     * reachable by Tab. A rule aimed only at how the gutter LOOKS would leave that alive. The stylesheet
     * therefore neutralises both: `pointer-events: none` on the gutter (which takes its `col-resize`
     * cursor with it, since the cursor then comes from `.p-splitter`, which sets none) and
     * `display: none` on the handle, which is what takes a `tabindex="0"` element out of the focus order
     * and the a11y tree. The gutter keeps `role="separator"`, correctly: an ARIA separator is a widget
     * only while it is focusable, and a non-focusable one is exactly what this now is — a static divider.
     *
     * {@link minSizes} means nothing while this is false; see its own note.
     */
    @Input() resizable: boolean = true;

    /**
     * When set, the host binds to the viewport: `height = calc(100vh - {viewportOffsetPx}px)`, so each
     * pane owns its own scroll and the page itself never scrolls. The offset is the fixed chrome above
     * the host (topbar + content padding). Omit to fill the parent instead (the parent must then be
     * height-bound).
     *
     * IGNORED when `autoFitViewport` is true and a measurement has been taken — see the class TSDoc's
     * height contract. It still serves as the pre-measurement fallback in that mode, so a consumer may
     * keep it as a first-paint approximation, but it is not required.
     */
    @Input() viewportOffsetPx?: number;

    /**
     * OPT-IN self-measuring height. When true the host height is MEASURED at runtime rather than derived
     * from a consumer-supplied constant (see the class TSDoc for the exact formula and precedence). Use
     * this whenever the chrome ABOVE the host is not constant — e.g. the app shell's optional breadcrumb,
     * which is present after a hard reload of one route and absent on another, and is sticky across
     * client-side navigation, so no single constant can be correct in both states.
     *
     * TOGGLEABLE AT RUNTIME. `[autoFitViewport]="ready"` is supported in BOTH directions: flipping it on
     * starts the measurement machinery, flipping it off tears down every listener/observer/animation frame
     * AND drops the measured value, so the host falls back to the static `viewportOffsetPx` / `100%`
     * branch. Start and stop are idempotent, so nothing is ever registered twice.
     *
     * Defaults to false, so existing consumers keep the static `calc(100vh - Npx)` / `100%` behaviour with
     * no runtime machinery at all.
     */
    @Input() autoFitViewport: boolean = false;

    /**
     * The gap BELOW the host inside the scrolling container, subtracted in auto-fit mode. For the bm
     * payment-reconciliation pages this is `.layout-content`'s `padding-bottom: 2rem`.
     *
     * Expressed in REM ON PURPOSE. The shell's content padding is authored in rem and resolves against
     * `html { font-size }`, which the app's UI-scale control (`app.config.component.ts`) rewrites at
     * runtime to 12/13/14/15/16px. The gap is therefore stable in REM but NOT in px — a px input would be
     * correct at exactly one scale and would overflow the viewport (the scrollbar comes back) at every
     * larger one. The rem value is multiplied by the CURRENT root font size at MEASURE TIME, so a scale
     * change re-resolves it rather than baking it in.
     *
     * Only read when `autoFitViewport` is true. Pass `[viewportBottomGapRem]="2"` for `padding: 2rem`.
     */
    @Input() viewportBottomGapRem: number = 0;

    /**
     * OPT-IN: below PrimeFlex's `lg` breakpoint (992px) the panes STACK — worklist above, workbench
     * below — instead of standing side by side. Defaults to false, so every existing consumer renders
     * byte-identically at EVERY width until it opts in: each rule of the stacked band requires the
     * `rw-stacked` class, and that class is emitted only for a consumer that binds this input.
     *
     * <h3>Why a splitter needs this at all</h3>
     * The splitter is always horizontal, so at 768px CSS px (an iPad mini in portrait) a 34/66 split is
     * ~248/484px. Measured on the cw create page: a row's logo + gaps + padding spend ~124px of the left
     * pane, leaving less than the width of one unbreakable word („KRANKENVERSICHERUNG") — the pane then
     * scrolls SIDEWAYS. Stacking gives each pane the full width instead.
     *
     * <h3>`lg` and not `md`</h3>
     * 992px, so an iPad mini stacks in PORTRAIT (768) and keeps the side-by-side layout in LANDSCAPE
     * (1024, where the same content measures ~335/653px and fits). 768 is PrimeFlex's `md` boundary
     * exactly, and this input deliberately does not use it: the gate is "below `lg`", which 768 clears
     * by 224px.
     *
     * <h3>A VIEWPORT query, not a container query</h3>
     * Stacking is a judgement about the DEVICE SHAPE — "is there height to spare?" — and not about how
     * wide this particular box happens to be. At 1024 landscape with the shell's sidebar expanded the
     * container is ~770px, so a `@container` query would stack a screen that is only 768px TALL, which
     * is the one place stacking is clearly wrong. The accepted cost: a NESTED (R7 level-2) workbench
     * inside a narrow pane does not stack, because the viewport knows nothing about its pane.
     *
     * <h3>Pure CSS, and why that is not a shortcut</h3>
     * The band re-lays-out the SAME horizontal splitter (the `rw-single` / `rw-collapse-*` mechanic,
     * applied to the axis) rather than switching PrimeNG's own `layout` input. Two reasons, both
     * measured against the library rather than assumed:
     * <ul>
     *   <li>`layout`'s horizontal branch emits NO `flex-direction` at all (`inlineStyles.root` yields an
     *       empty entry), and PrimeNG applies those styles through a `[style]` host binding. Switching
     *       to `'vertical'` and back would therefore leave a stale inline `flex-direction: column`
     *       behind — the panes would stay stacked after a rotation into landscape.</li>
     *   <li>CSS keeps BOTH projected embedded views alive across the breakpoint by construction. There
     *       is no structure to re-create, so a half-made selection cannot be lost to a resize — the same
     *       hard requirement {@link collapsePane} carries.</li>
     * </ul>
     *
     * <h3>The scroll contract survives; only its axis changes</h3>
     * Each pane becomes an equal share of the HOST HEIGHT and keeps its own `overflow-y: auto`, and the
     * page still never scrolls. What gives way is "each pane is full-viewport-height", not "each pane
     * owns its scroll" — because the second is what every consumer's projected panel, its centred empty
     * state and any flex-height table depend on. `panelSizes` / `minSizes` deliberately do NOT carry
     * over: as widths they answer "how much room does this content need", and as heights they answer
     * nothing (a `minSizes` of 24% is a 180px sliver, not a pane).
     */
    @Input() stackBelowLg: boolean = false;

    /**
     * Pane content templates. Consumers project each pane via a NAMED `<ng-template>`
     * (`#workbenchList` / `#workbenchDetail` / `#workbenchCenter`) rather than an attribute slot.
     *
     * WHY templates (not `<ng-content>`): a `<p-splitter>` renders each pane inside its own
     * `<ng-template pTemplate>` embedded view, and Angular `<ng-content>` DOES NOT project into an
     * embedded view — the projected nodes never appear, so both panes render empty. `TemplateRef` +
     * `*ngTemplateOutlet` DOES instantiate inside an embedded view, so the content renders correctly.
     */
    @ContentChild('workbenchList', {read: TemplateRef}) listTpl?: TemplateRef<unknown>;
    @ContentChild('workbenchDetail', {read: TemplateRef}) detailTpl?: TemplateRef<unknown>;
    @ContentChild('workbenchCenter', {read: TemplateRef}) centerTpl?: TemplateRef<unknown>;

    /** Fallback root font size when the computed value is unreadable/zero (matches the CSS initial value). */
    private static readonly FALLBACK_ROOT_FONT_PX = 16;

    /**
     * Memo cells for {@link resolvedPanelSizes} / {@link resolvedMinSizes} — the arrays LAST handed to the
     * splitter, kept so an unchanged value keeps an unchanged identity. See `resolvedPanelSizes` for why
     * that identity is what keeps the gutter draggable.
     */
    private stablePanelSizes?: number[];
    private stableMinSizes?: number[];

    /**
     * The last MEASURED host height in px, or null while auto-fit is off / not yet measured / not
     * measurable (hidden host, non-browser environment). Private internal state of the auto-fit mode —
     * the specs reach it via `component['measuredHeightPx']`.
     */
    private measuredHeightPx: number | null = null;

    /**
     * ONE ResizeObserver watching the host's whole ANCESTOR CHAIN — every element from the host's parent
     * up to and including `document.body`.
     *
     * WHY THE WHOLE CHAIN (U-1). The quantity this component depends on is the host's TOP edge, and in
     * normal flow that edge can only move when the box of one of its ancestors changes: chrome above the
     * host is either a preceding sibling of the host or a preceding sibling of one of its ancestors, and
     * in both cases the enclosing ancestor's own box changes with it. Observing the chain is therefore
     * the complete, app-shell-agnostic signal — no class names, no knowledge of the shell's markup.
     *
     * Watching only the parent and `document.body` (the previous design) was NOT complete, and the gap
     * was one-directional in the worst possible way:
     *   * GROWTH was caught — inserting chrome above the host pushes the still-unshrunk host past the
     *     viewport, `body` grows beyond its `min-height:100%` floor and an entry is delivered.
     *   * SHRINKING was NOT — when that chrome collapses, total content falls BELOW the viewport, `body`
     *     is pinned at the viewport height by `html{height:100%} + body{min-height:100%}` and delivers
     *     nothing, the direct parent's inline size is unchanged, and no window resize occurs.
     * So the host gave space up when asked and never took it back: measured on the Beratungsdokumentation
     * (cw), collapsing the closing-blocker list freed 240 px of header that the capture surface never
     * received — 268 px / 29.8 % dead area at 1440x900. The intermediate flow container holding header +
     * workbench DOES change its box in that direction; it is simply not the parent and not body.
     *
     * ECHO. The height we write onto the host changes the box of every content-sized ancestor, so one
     * further delivery always follows. It costs one animation frame: the re-measure converges to the same
     * value and `applyMeasuredHeight` returns without re-entering the Angular zone. A layout that does
     * NOT converge is caught by `measurementSuspended`.
     */
    private ancestorResizeObserver?: ResizeObserver;

    /** Pending requestAnimationFrame handle; coalesces a burst of observer/resize ticks into one measure. */
    private measureFrameHandle: number | null = null;

    /**
     * The heights applied since the last time a measurement CONVERGED (i.e. came back unchanged).
     *
     * In a well-behaved layout this holds at most one entry: we write a height, the echo re-measures to
     * the same number, the list is cleared. It grows only while our own write keeps changing the input it
     * is derived from — which is the shape of an endless loop, and what `measurementSuspended` uses it to
     * detect.
     */
    private readonly appliedHeightCycle: number[] = [];

    /**
     * Set when the measurement was found to feed itself: a height we already applied in the current
     * un-converged burst came back as the next measurement (A → B → A …), or the burst simply refused to
     * settle. Possible when an ancestor re-positions the host in response to the host's own height
     * (a vertically centring container, say).
     *
     * While set, observer-driven measurements are ignored, so the component sits at the last height it
     * applied rather than flipping forever. It is cleared by a REAL external trigger — a viewport
     * resize/orientation change, or a fresh `startAutoFit()` — because those change the inputs
     * independently of anything we wrote.
     */
    private measurementSuspended = false;

    /** Hard cap on one un-converged burst, in case it drifts through new values instead of alternating. */
    private static readonly MAX_UNSETTLED_APPLICATIONS = 8;

    /** True once the view exists, i.e. once the host can meaningfully be measured. */
    private viewInitialised = false;

    /** True while the listeners/observers are registered; makes start and stop idempotent. */
    private autoFitStarted = false;

    /**
     * Stable listener reference so teardown can remove exactly what the start registered.
     *
     * A viewport resize is an EXTERNAL input change, so it also lifts a self-feeding suspension: whatever
     * made the previous burst oscillate, the numbers it oscillated between are stale now.
     */
    private readonly onViewportChange = (): void => {
        this.resumeMeasurement();
        this.scheduleMeasure();
    };

    constructor(
        private readonly hostRef: ElementRef<HTMLElement>,
        private readonly zone: NgZone,
        private readonly changeDetectorRef: ChangeDetectorRef,
    ) {
    }

    @HostBinding('style.display') readonly display = 'block';
    @HostBinding('style.overflow') readonly overflow = 'hidden';

    /**
     * The SINGLE place that decides the host height — see the class TSDoc for the full contract. Auto-fit
     * (a measured px value) wins over `viewportOffsetPx`; the static `calc(100vh - Npx)` and fill-parent
     * `100%` branches are unchanged, so a consumer that sets neither new input renders the same string as
     * before.
     */
    @HostBinding('style.height')
    get hostHeight(): string {
        if (this.autoFitViewport && this.measuredHeightPx != null) {
            return `${this.measuredHeightPx}px`;
        }
        return this.viewportOffsetPx != null ? `calc(100vh - ${this.viewportOffsetPx}px)` : '100%';
    }

    /**
     * Runtime toggling of `autoFitViewport`. Changes that arrive BEFORE the view exists are ignored here
     * on purpose — `ngAfterViewInit` performs the initial start, because the host must be laid out before
     * its top edge means anything.
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (!changes['autoFitViewport'] || !this.viewInitialised) {
            return;
        }
        this.syncAutoFit();
    }

    ngAfterViewInit(): void {
        this.viewInitialised = true;
        this.syncAutoFit();
    }

    ngOnDestroy(): void {
        this.teardownMeasurement();
    }

    /** True when only the worklist is shown; drives the `rw-single` collapse class. */
    get singlePane(): boolean {
        return this.panes === 1;
    }

    /**
     * Drives `rw-no-resize`: the split is FIXED — the gutter is a spacer rather than a drag handle.
     *
     * Stated as the negation of {@link resizable} in ONE place so the template never spells out a `!`,
     * and so the default (`true`) yields `false` here and therefore emits no class at all — which is
     * what keeps every consumer that does not bind the input rendering byte-identically.
     */
    get fixedSplit(): boolean {
        return !this.resizable;
    }

    /**
     * The pane that is EFFECTIVELY collapsed right now — `collapsePane`, normalised to `null` and forced to
     * `null` in 1-pane mode (see the `collapsePane` TSDoc for why).
     */
    get collapsedPane(): 'list' | 'detail' | null {
        if (this.panes === 1) {
            return null;
        }
        return this.collapsePane ?? null;
    }

    /** Drives `rw-collapse-list`: the first (worklist) pane is collapsed. */
    get collapseList(): boolean {
        return this.collapsedPane === 'list';
    }

    /** Drives `rw-collapse-detail`: the last (workbench) pane is collapsed. */
    get collapseDetail(): boolean {
        return this.collapsedPane === 'detail';
    }

    /**
     * Drives `rw-collapsible` — true as soon as a consumer BINDS `collapsePane`, even to `null`.
     *
     * It gates the transition rules ONLY. Those must stay off for a consumer that never bound the input,
     * because a `transition` on `.p-splitterpanel` also animates the inline `flex-basis` PrimeNG writes on
     * every gutter drag, which would change how dragging feels for all seven existing consumers. Gating on
     * "was the input bound" (rather than "is a pane collapsed right now") is what makes the transition run
     * in BOTH directions — collapsing AND expanding back to `null`.
     */
    get collapsible(): boolean {
        return this.collapsePane !== undefined;
    }

    /**
     * The pane sizes handed to `<p-splitter [panelSizes]>`, with a STABLE ARRAY IDENTITY.
     *
     * The identity is not cosmetic — it is what makes the gutter draggable at all. PrimeNG's
     * `panelSizes` SETTER rewrites the inline `flex-basis` of every panel back to the bound values, and
     * Angular calls that setter whenever the bound expression yields a NEW OBJECT, whatever its contents.
     * A drag is a burst of mousemoves, each one a change-detection cycle: PrimeNG writes the dragged width
     * in `onResize`, the same cycle re-evaluates this getter, a fresh array arrives, and the setter puts
     * the old width straight back. The gutter then follows the pointer, the panels never move, and the
     * splitter looks broken while every internal step reports success — measured live on the
     * Beratungsdokumentation one-pager: 20 setter calls with 20 distinct array identities in ONE drag, and
     * neutralising only that rewrite made the identical drag work (938/705 px → 788/855 px).
     *
     * So the identity has to be stable across cycles in which the VALUES did not change, and it is stable
     * here rather than at the consumer for two reasons: a template literal (`[panelSizes]="[66, 34]"`) is
     * already cached by Angular and unaffected, while the two shapes that are NOT — a consumer getter that
     * builds its array per call, and the defaults below, which did exactly the same — are both absorbed in
     * one place instead of being re-fixed in every host.
     *
     * The cache is keyed on VALUE equality, so a real size change still reaches the splitter and still
     * resets the panes, which is what a real size change is supposed to do.
     */
    get resolvedPanelSizes(): number[] {
        const next = this.panelSizes && this.panelSizes.length
            ? this.panelSizes
            : (this.panes === 3 ? [22, 48, 30] : [66, 34]);
        return this.stablePanelSizes = MvsReviewWorkbenchComponent.stableArray(this.stablePanelSizes, next);
    }

    /** Minimum pane sizes, identity-stabilised for the same reason as {@link resolvedPanelSizes}. */
    get resolvedMinSizes(): number[] {
        const next = this.minSizes && this.minSizes.length
            ? this.minSizes
            : (this.panes === 3 ? [15, 35, 25] : [30, 22]);
        return this.stableMinSizes = MvsReviewWorkbenchComponent.stableArray(this.stableMinSizes, next);
    }

    /**
     * Keep the PREVIOUS array while it is still element-wise equal to the new one, so a getter that
     * rebuilds an equal array on every change-detection cycle stops looking like a change.
     */
    private static stableArray(cached: number[] | undefined, next: number[]): number[] {
        if (cached && cached.length === next.length && cached.every((value, i) => value === next[i])) {
            return cached;
        }
        return next;
    }

    /** Bring the measurement machinery in line with the current `autoFitViewport` value. */
    private syncAutoFit(): void {
        if (this.autoFitViewport) {
            this.startAutoFit();
        } else {
            this.stopAutoFit();
        }
    }

    /**
     * Register the measurement machinery. Idempotent — a repeated call while already started registers
     * nothing. Does nothing at all when the browser APIs are missing (SSR/test), so the component falls
     * back to the static branches rather than throwing.
     */
    private startAutoFit(): void {
        if (this.autoFitStarted || !this.canMeasure()) {
            return;
        }
        this.autoFitStarted = true;
        this.resumeMeasurement();

        // Outside the zone: a ResizeObserver or a resize burst must not schedule a change-detection
        // cycle per tick. We re-enter the zone only when the computed height actually changes.
        this.zone.runOutsideAngular(() => {
            window.addEventListener('resize', this.onViewportChange, {passive: true});
            window.addEventListener('orientationchange', this.onViewportChange, {passive: true});

            if (typeof ResizeObserver !== 'undefined') {
                const chain = this.ancestorChain();
                if (chain.length) {
                    this.ancestorResizeObserver = new ResizeObserver(() => this.scheduleMeasure());
                    for (const ancestor of chain) {
                        this.ancestorResizeObserver.observe(ancestor, {box: 'border-box'});
                    }
                }
            }

            this.scheduleMeasure();
        });
    }

    /**
     * The host's ancestors from its parent up to and including `document.body` — the complete set of
     * elements whose box can move the host's top edge in normal flow. Empty when the host is detached or
     * the DOM is unavailable (SSR).
     */
    private ancestorChain(): HTMLElement[] {
        const chain: HTMLElement[] = [];
        if (typeof document === 'undefined') {
            return chain;
        }
        const body = document.body;
        let current: HTMLElement | null = this.hostRef.nativeElement?.parentElement ?? null;
        while (current) {
            chain.push(current);
            if (current === body) {
                break;
            }
            current = current.parentElement;
        }
        return chain;
    }

    /**
     * Tear the machinery down AND drop the measured value, so `hostHeight` falls straight back to the
     * static `viewportOffsetPx` / `100%` branch. Idempotent.
     */
    private stopAutoFit(): void {
        this.teardownMeasurement();
        if (this.measuredHeightPx !== null) {
            this.measuredHeightPx = null;
            this.changeDetectorRef.markForCheck();
        }
    }

    /** Remove every listener, observer and pending frame this component registered. Idempotent. */
    private teardownMeasurement(): void {
        if (typeof window !== 'undefined') {
            window.removeEventListener('resize', this.onViewportChange);
            window.removeEventListener('orientationchange', this.onViewportChange);
        }

        this.ancestorResizeObserver?.disconnect();
        this.ancestorResizeObserver = undefined;

        if (this.measureFrameHandle !== null && typeof cancelAnimationFrame !== 'undefined') {
            cancelAnimationFrame(this.measureFrameHandle);
        }
        this.measureFrameHandle = null;
        this.autoFitStarted = false;
        this.resumeMeasurement();
    }

    /** Clear a self-feeding suspension and the burst it was detected from. */
    private resumeMeasurement(): void {
        this.measurementSuspended = false;
        this.appliedHeightCycle.length = 0;
    }

    /** True only where the browser APIs the auto-fit mode needs actually exist (never on SSR). */
    private canMeasure(): boolean {
        return typeof window !== 'undefined' && typeof requestAnimationFrame !== 'undefined';
    }

    /**
     * Coalesce every trigger (resize burst, observer tick) into ONE measurement per animation frame, and
     * take it inside the frame so reading the host rect never causes layout thrash mid-observer-callback.
     */
    private scheduleMeasure(): void {
        if (this.measureFrameHandle !== null || this.measurementSuspended || !this.canMeasure()) {
            return;
        }
        this.measureFrameHandle = requestAnimationFrame(() => {
            this.measureFrameHandle = null;
            this.applyMeasuredHeight();
        });
    }

    /**
     * Measure, and re-enter the Angular zone ONLY when the resulting height really changed.
     *
     * Also the place the endless-loop guard lives, because this is the only place a height is written and
     * therefore the only place our own output can be seen coming back as our input. A measurement equal to
     * the current height means the layout has CONVERGED (the ordinary case — it is the echo of our last
     * write); a measurement equal to one we already applied in this un-converged burst means it has not
     * and will not, so we stop instead of flipping forever.
     */
    private applyMeasuredHeight(): void {
        const next = this.resolveMeasuredHeight();
        if (next === null) {
            return;
        }
        if (next === this.measuredHeightPx) {
            this.appliedHeightCycle.length = 0;
            return;
        }
        if (this.appliedHeightCycle.includes(next)
            || this.appliedHeightCycle.length >= MvsReviewWorkbenchComponent.MAX_UNSETTLED_APPLICATIONS) {
            this.measurementSuspended = true;
            this.appliedHeightCycle.length = 0;
            return;
        }
        this.appliedHeightCycle.push(next);
        this.zone.run(() => {
            this.measuredHeightPx = next;
            // Host bindings are evaluated by the declaring (parent) view, so mark the path dirty and let
            // the tick this zone.run triggers re-evaluate `hostHeight`.
            this.changeDetectorRef.markForCheck();
        });
    }

    /**
     * The available height between the host's top edge and the bottom of the viewport, minus the bottom
     * gap resolved from rem at THIS moment. Floored so a fractional host top (e.g. 123.8px) can never
     * round the host INTO an overflow, and so sub-pixel jitter does not churn change detection.
     *
     * Returns null when the host is not rendered (`offsetParent === null`, e.g. inside a hidden tab
     * panel): its rect would read a top of 0 and blow the host up to the full viewport height, and that
     * bogus value would survive until the next trigger after the panel becomes visible.
     */
    private resolveMeasuredHeight(): number | null {
        if (!this.canMeasure()) {
            return null;
        }
        const host = this.hostRef?.nativeElement;
        if (!host || host.offsetParent === null) {
            return null;
        }
        const top = host.getBoundingClientRect().top;
        // clientHeight, NOT innerHeight: innerHeight includes the horizontal scrollbar gutter, which would
        // let a document-level horizontal scrollbar reintroduce vertical overflow.
        const viewportHeight = document.documentElement.clientHeight;
        return Math.max(0, Math.floor(viewportHeight - top - this.resolveBottomGapPx()));
    }

    /**
     * The bottom gap in px, resolved from rem against the CURRENT root font size — which the app's
     * UI-scale control rewrites at runtime, so this must never be cached. Falls back to the CSS initial
     * 16px when the computed value is missing, NaN or zero.
     */
    private resolveBottomGapPx(): number {
        if (!this.viewportBottomGapRem) {
            return 0;
        }
        const computed = parseFloat(getComputedStyle(document.documentElement).fontSize);
        const rootFontPx = Number.isFinite(computed) && computed > 0
            ? computed
            : MvsReviewWorkbenchComponent.FALLBACK_ROOT_FONT_PX;
        return this.viewportBottomGapRem * rootFontPx;
    }
}
